import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { TrendingUp, Eye, Film, MessageSquare, Users, Image, BarChart3 } from 'lucide-react';

const AnalyticsDashboard = () => {
  const { api } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/admin/analytics');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="admin-loading">Loading analytics...</div>;
  if (!analytics) return <div>No analytics data</div>;

  const { overview, top_videos, recent_videos, recent_comments } = analytics;

  return (
    <div className="admin-content">
      <div className="admin-header">
        <div>
          <h1>Analytics & Reports</h1>
          <p className="admin-subtitle">Track your platform's performance and engagement</p>
        </div>
      </div>

      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(6,182,212,0.1)' }}>
            <Film size={24} style={{ color: '#06b6d4' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Videos</p>
            <h3 className="stat-value">{overview.total_videos}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <Eye size={24} style={{ color: '#10b981' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Views</p>
            <h3 className="stat-value">{overview.total_views.toLocaleString()}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(139,92,246,0.1)' }}>
            <MessageSquare size={24} style={{ color: '#8b5cf6' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Comments</p>
            <h3 className="stat-value">{overview.total_comments}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(236,72,153,0.1)' }}>
            <Users size={24} style={{ color: '#ec4899' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Users</p>
            <h3 className="stat-value">{overview.total_users}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(251,191,36,0.1)' }}>
            <Image size={24} style={{ color: '#fbbf24' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Active Banners</p>
            <h3 className="stat-value">{overview.total_banners}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <BarChart3 size={24} style={{ color: '#ef4444' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Banner Clicks</p>
            <h3 className="stat-value">{overview.total_banner_clicks}</h3>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div className="admin-card">
          <h2 className="card-title">Top Performing Videos</h2>
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Views</th>
                </tr>
              </thead>
              <tbody>
                {top_videos.map((video, idx) => (
                  <tr key={video.id || idx}>
                    <td>{video.title}</td>
                    <td>{video.views.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="card-title">Recent Activity</h2>
          <div style={{ fontSize: '14px' }}>
            <h4 style={{ marginBottom: '12px', color: '#06b6d4' }}>Recent Videos</h4>
            {recent_videos.map((video, idx) => (
              <div key={video.id || idx} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <strong>{video.title}</strong>
                <div style={{ color: '#888', fontSize: '12px' }}>{new Date(video.created_at).toLocaleDateString()}</div>
              </div>
            ))}
            
            <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#8b5cf6' }}>Recent Comments</h4>
            {recent_comments.map((comment, idx) => (
              <div key={comment.id || idx} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <strong>{comment.user || 'Anonymous'}</strong>
                <div style={{ color: '#888', fontSize: '12px' }}>{comment.text?.substring(0, 50)}...</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;